import { useState, type FunctionComponent } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from "antd";

import {
  type ChallengeTaskConfig,
  type ChallengeTaskEntity,
  ChallengeTaskType,
  type ChallengeVerification,
  getChallengeRecordingEventDates,
} from "@fitness-app/data-models/entities/Challenge";

import { supabase } from "~/store/initializeStore";
import ChallengeTaskFormModal from "./ChallengeTaskFormModal";
import { challengeTaskTypeLabels, challengeVerificationLabels } from "./constants";
import { formatChallengeDate } from "./helpers";

type Props = {
  challengeId: string;
};

const terminyLabel = (count: number): string => {
  if (count === 1) {
    return "termin";
  }
  const mod10 = count % 10;
  const mod100 = count % 100;
  return mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "terminy" : "terminów";
};

const ChallengeTasksTable: FunctionComponent<Props> = ({ challengeId }) => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ChallengeTaskEntity | null>(null);

  const tasksQuery = useQuery({
    queryKey: ["challenge-tasks", challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_task")
        .select("*")
        .eq("challengeId", challengeId)
        .order("sortOrder", { ascending: true })
        .returns<ChallengeTaskEntity[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("challenge_task").delete().eq("id", id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Zadanie usunięte");
      void queryClient.invalidateQueries({ queryKey: ["challenge-tasks", challengeId] });
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się usunąć zadania: ${error.message}`);
    },
  });

  const tasks = tasksQuery.data ?? [];
  const nextSortOrder =
    tasks.length > 0 ? Math.max(...tasks.map((task) => task.sortOrder ?? 0)) + 1 : 0;

  const openCreate = () => {
    setEditingTask(null);
    setIsFormOpen(true);
  };

  const openEdit = (task: ChallengeTaskEntity) => {
    setEditingTask(task);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingTask(null);
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Dodaj zadanie
        </Button>
      </div>

      <Table<ChallengeTaskEntity>
        dataSource={tasks}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={tasksQuery.isFetching}
        locale={{ emptyText: "Brak zadań w tym wyzwaniu" }}
        pagination={false}
      >
        <Table.Column<ChallengeTaskEntity>
          title="Zadanie"
          dataIndex="title"
          key="title"
          render={(title, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{title}</Typography.Text>
              {row.description ? (
                <Typography.Text type="secondary" style={{ maxWidth: 360 }}>
                  {row.description}
                </Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<ChallengeTaskEntity>
          title="Typ"
          dataIndex="type"
          key="type"
          render={(type: ChallengeTaskType) => (
            <Tag color="geekblue">{challengeTaskTypeLabels[type] ?? type}</Tag>
          )}
        />
        <Table.Column<ChallengeTaskEntity>
          title="Weryfikacja"
          dataIndex="verification"
          key="verification"
          render={(verification: ChallengeVerification) => (
            <Tag>{challengeVerificationLabels[verification] ?? verification}</Tag>
          )}
        />
        <Table.Column<ChallengeTaskEntity>
          title="Punkty"
          dataIndex="points"
          key="points"
          render={(points: number) => `${points} pkt`}
        />
        <Table.Column<ChallengeTaskEntity>
          title="Cel"
          key="target"
          render={(_, row) =>
            row.target ? `${row.target}${row.unit ? ` ${row.unit}` : ""}` : <Typography.Text type="secondary">-</Typography.Text>
          }
        />
        <Table.Column<ChallengeTaskEntity>
          title="Dni"
          key="daysOfWeek"
          render={(_, row) => {
            const config = row.config as ChallengeTaskConfig | null;
            // Recording tasks are scheduled on specific dates, not a weekday pattern.
            if (row.type === ChallengeTaskType.ExerciseRecording) {
              const eventDates = [...getChallengeRecordingEventDates(config)].sort();
              if (!eventDates.length) {
                return <Typography.Text type="secondary">-</Typography.Text>;
              }
              const formatted = eventDates.map((date) => formatChallengeDate(date));
              if (eventDates.length <= 3) {
                return <Tag color="gold">{formatted.join(", ")}</Tag>;
              }
              return (
                <Tooltip title={formatted.join(", ")}>
                  <Tag color="gold">
                    {`${eventDates.length} ${terminyLabel(eventDates.length)}: ${formatted[0]} – ${
                      formatted[formatted.length - 1]
                    }`}
                  </Tag>
                </Tooltip>
              );
            }
            const days = config?.daysOfWeek;
            if (!Array.isArray(days) || days.length === 0) {
              return <Typography.Text type="secondary">Codziennie</Typography.Text>;
            }
            // dayjs numbering (0 = Sunday), rendered Monday-first.
            const labels = ["Niedz", "Pon", "Wt", "Śr", "Czw", "Pt", "Sob"];
            const ordered = [1, 2, 3, 4, 5, 6, 0].filter((day) => days.includes(day));
            return <Tag color="gold">{ordered.map((day) => labels[day]).join(", ")}</Tag>;
          }}
        />
        <Table.Column<ChallengeTaskEntity>
          title="Opcje"
          key="action"
          render={(_, row) => (
            <Space size="middle">
              <Button type="link" className="px-0" onClick={() => openEdit(row)}>
                Edytuj
              </Button>
              <Popconfirm
                title="Usunąć zadanie?"
                okText="Usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true, loading: deleteTaskMutation.isLoading }}
                onConfirm={() => deleteTaskMutation.mutate(row.id)}
              >
                <Button type="link" danger className="px-0">
                  Usuń
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <ChallengeTaskFormModal
        open={isFormOpen}
        challengeId={challengeId}
        task={editingTask}
        nextSortOrder={nextSortOrder}
        onClose={closeForm}
      />
    </Space>
  );
};

export default ChallengeTasksTable;
